import type { AcademyState, AttendanceStatus, Belt, Ranking } from './types';

export const belts: Belt[] = ['White', 'Blue', 'Purple', 'Brown', 'Black'];
export const attendanceStatuses: AttendanceStatus[] = ['present', 'late', 'excused', 'absent'];

export function rankingPoints(attendance90: number, late90 = 0, excused90 = 0): number {
  return attendance90 * 10 + late90 * 5 + excused90 * 2;
}

export function academyRankings(state: AcademyState): Ranking[] {
  return state.students
    .map((student) => ({ ...student, points: student.points ?? rankingPoints(student.attendance90, student.late90, student.excused90) }))
    .sort((a, b) => b.points - a.points || b.attendance90 - a.attendance90 || a.name.localeCompare(b.name))
    .map((student, index) => ({ ...student, position: index + 1 }));
}

export function updateAttendance(
  state: AcademyState,
  sessionId: string,
  studentId: string,
  status: AttendanceStatus,
): AcademyState {
  const session = state.sessions.find((item) => item.id === sessionId);
  const previous = session?.attendance[studentId];
  const startsAt = session ? new Date(session.startsAt).getTime() : 0;
  const now = Date.now();
  const withinWindow = startsAt >= now - 90 * 86400000 && startsAt <= now;
  const adjust = (value: number | undefined, target: AttendanceStatus) =>
    Math.max(0, (value ?? 0) - (withinWindow && previous === target ? 1 : 0) + (withinWindow && status === target ? 1 : 0));
  return {
    ...state,
    students: state.students.map((student) => student.id === studentId
      ? {
          ...student,
          attendance90: adjust(student.attendance90, 'present'),
          late90: adjust(student.late90, 'late'),
          excused90: adjust(student.excused90, 'excused'),
          points: undefined,
        }
      : student),
    sessions: state.sessions.map((session) =>
      session.id === sessionId
        ? { ...session, attendance: { ...session.attendance, [studentId]: status } }
        : session,
    ),
  };
}

export function promoteStudent(
  state: AcademyState,
  studentId: string,
  belt: Belt,
  stripes: number,
  awardedOn: string,
  note?: string,
): AcademyState {
  if (stripes < 0 || stripes > 4) throw new Error('Stripes must be between 0 and 4.');
  if (!/^\d{4}-\d{2}-\d{2}$/.test(awardedOn)) throw new Error('Choose a valid effective date.');
  return {
    ...state,
    students: state.students.map((student) =>
      student.id === studentId ? {
        ...student,
        belt,
        stripes,
        progressions: [{ id: crypto.randomUUID(), belt, stripes, awardedOn, note: note?.trim() || undefined }, ...(student.progressions ?? [])],
      } : student,
    ),
  };
}
